import {
  type MutationKey,
  type QueryKey,
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
} from "@tanstack/react-query";
import type { RequestDocument } from "graphql-request";
import { graphqlRequest } from "@/lib/graphql/client";

type GraphqlQueryEnabled<TData> = UseQueryOptions<TData, Error, TData, QueryKey>["enabled"] | null;

export const useGraphqlQuery = <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  params: {
    queryKey: QueryKey;
    document: RequestDocument;
    variables?: TVariables;
    /**
     * Accepts `null` so callers can pass tri-state auth flags (e.g. `isAdmin`)
     * without `Boolean(...)` ceremony. `null` is treated as disabled.
     * Function form from React Query is preserved as-is.
     */
    enabled?: GraphqlQueryEnabled<TData>;
  } & Omit<UseQueryOptions<TData, Error, TData, QueryKey>, "queryKey" | "queryFn" | "enabled">,
) => {
  const { queryKey, document, variables, enabled, ...options } = params;

  return useQuery<TData, Error, TData, QueryKey>({
    ...options,
    queryKey,
    ...(enabled === undefined
      ? {}
      : {
          enabled: typeof enabled === "function" ? enabled : Boolean(enabled),
        }),
    queryFn: () => graphqlRequest<TData, TVariables>(document, variables),
  });
};

export const useGraphqlMutation = <TData, TVariables extends Record<string, unknown> = Record<string, never>>(
  params: {
    mutationKey?: MutationKey;
    document: RequestDocument;
  } & Omit<UseMutationOptions<TData, Error, TVariables, unknown>, "mutationFn" | "mutationKey">,
) => {
  const { mutationKey, document, ...options } = params;

  return useMutation<TData, Error, TVariables, unknown>({
    ...options,
    mutationKey,
    mutationFn: (variables) => graphqlRequest<TData, TVariables>(document, variables),
  });
};
